import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SxProps,
  Theme,
  Typography
} from '@mui/material';
import React from 'react';
import { useFoldersContext } from '../../contexts/useFolders.context';
import { Folder } from '../../types';
import { CreateNewFolder } from './CreateNewFolder';
import { FOLDER_DEPTH_LIMIT, useIsUnderDepthLimit } from './hooks/useIsUnderDepthLimit';
import { styles } from './LocationSelect.styles';

const intl = {
  label: 'Location',
  depthLimitWarning: (depthLimit: number) => `Folders may only be nested up to ${depthLimit} levels deep`
};

type LocationSelectProps = {
  selectedFolder: Folder;
  subfolders: Folder[];
  sx?: SxProps<Theme>;
  label?: string;
  withCreateNewFolder?: boolean;
  checkDepthLimit?: boolean;
  disabledFolderIds?: Folder['id'][];
  onFolderSelect: (folderId: Folder['id']) => void;
};

export function LocationSelect({
  sx,
  label = intl.label,
  withCreateNewFolder = false,
  checkDepthLimit = true,
  selectedFolder,
  subfolders,
  onFolderSelect,
  disabledFolderIds
}: LocationSelectProps) {
  const { rootFolder } = useFoldersContext();
  const { isUnderParentFolderDepthLimit, isUnderFolderDepthLimit } = useIsUnderDepthLimit({
    checkDepthLimit,
    selectedFolderId: selectedFolder.id,
    folders: rootFolder.subfolders || []
  });
  const isRootFolder = selectedFolder.id === rootFolder.id;

  return (
    <Box sx={{ ...styles.container, ...sx }} data-test="locationSelect">
      <Typography sx={styles.label} component="div">
        {label}
      </Typography>

      <Box sx={styles.listContainer}>
        <List dense sx={styles.list}>
          <ListItem sx={styles.listItem}>
            <ListItemButton
              selected={true}
              onClick={() => {
                if (!isRootFolder) {
                  onFolderSelect(selectedFolder.parentFolderId);
                }
              }}
            >
              <ListItemIcon>{isRootFolder ? <HomeIcon /> : <ChevronLeftIcon />}</ListItemIcon>
              <ListItemText
                primary={selectedFolder.title}
                primaryTypographyProps={{
                  sx: styles.parentListItemText,
                  noWrap: true,
                  component: 'span'
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>

        <List dense sx={styles.subfoldersList}>
          {subfolders.map((folder) => (
            <ListItem key={folder.id} sx={styles.listItem}>
              <ListItemButton
                key={folder.id}
                selected={selectedFolder.id === folder.id}
                disabled={disabledFolderIds?.includes(folder.id) || !isUnderParentFolderDepthLimit}
                onClick={() => {
                  if (isUnderParentFolderDepthLimit) {
                    onFolderSelect(folder.id);
                  }
                }}
              >
                <ListItemIcon />
                <ListItemText
                  primary={folder.title}
                  primaryTypographyProps={{
                    sx: styles.subfolderListItemText,
                    noWrap: true,
                    component: 'span'
                  }}
                />
                {isUnderParentFolderDepthLimit ? (
                  <ListItemIcon>
                    <ChevronRightIcon />
                  </ListItemIcon>
                ) : null}
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {withCreateNewFolder && isUnderFolderDepthLimit ? (
          <>
            <Divider />
            <Box sx={styles.createNewFolderContainer}>
              <CreateNewFolder
                parentFolderId={selectedFolder.id}
                subfolders={subfolders}
                onCreateFolder={(folderId) => {
                  if (isUnderParentFolderDepthLimit) {
                    onFolderSelect(folderId);
                  }
                }}
              />
            </Box>
          </>
        ) : null}
      </Box>

      {!isUnderParentFolderDepthLimit ? (
        <Typography sx={styles.depthLimitWarning} variant="bodySmall" component="div" data-test="depthLimitWarning">
          <WarningIcon sx={styles.depthLimitWarningIcon} /> {intl.depthLimitWarning(FOLDER_DEPTH_LIMIT)}
        </Typography>
      ) : null}
    </Box>
  );
}
